
import { ArrowLeft, Shield, Eye, MapPin, MessageCircle, Camera, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import AdPanel from '@/components/ads/AdPanel';
import LegalFooter from '@/components/common/LegalFooter';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-carbon">
      <AdPanel />
      
      <div className="p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-6">
            <Button
              onClick={handleBack}
              variant="ghost"
              size="sm"
              className="mr-3 text-ice-white hover:bg-ice-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center">
              <img 
                src="/lovable-uploads/8c71c5e9-9b7a-44a8-8098-652cabdedf5f.png" 
                alt="ALPHA Logo" 
                className="w-8 h-8 mr-3"
              />
              <h1 className="text-2xl font-bold text-ice-white">Privacy Policy</h1>
            </div>
          </div>

          <Card className="bg-carbon border-ice-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-ice-white">Last Updated: June 24, 2025</CardTitle>
            </CardHeader>
            <CardContent className="text-ice-white/80 space-y-4">
              <p>
                At ALPHA, we are committed to protecting your privacy and creating a safe space for LGBTQ+ 
                individuals to connect. This Privacy Policy explains how we collect, use, and protect your personal information.
              </p>
              <div className="flex items-start space-x-3 p-4 bg-electric-violet/10 rounded-lg border border-electric-violet/20">
                <Shield className="w-5 h-5 text-electric-violet flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-ice-white mb-1">LGBTQ+ Privacy Commitment</p>
                  <p>We understand the unique privacy needs of our community and are committed to protecting your identity and personal information with the highest standards of security.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <Eye className="w-5 h-5 text-neon-red" />
                  1. Information We Collect
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-4">
                <div>
                  <p className="font-semibold mb-2">Account Information:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Email address and authentication data</li>
                    <li>Display name and age</li>
                    <li>Sexual orientation and gender identity (optional)</li>
                    <li>Profile photos and bio information</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold mb-2">Preference Data:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Age range preferences</li>
                    <li>Location preferences and search radius</li>
                    <li>Interests and lifestyle preferences</li>
                    <li>Privacy settings and notification preferences</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold mb-2">Usage Information:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>App usage patterns and feature interactions</li>
                    <li>Messages and communication history</li>
                    <li>Profile views and matching activity</li>
                    <li>Device information and IP addresses</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <MapPin className="w-5 h-5 text-neon-red" />
                  2. Location Information
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>How We Use Location:</strong> We use your location to show you nearby matches and calculate distances to other users.</p>
                <p><strong>Location Accuracy:</strong> We use city-level location data rather than precise GPS coordinates for privacy protection.</p>
                <p><strong>Location Control:</strong> You can update your location manually or disable location-based matching entirely.</p>
                <p><strong>Travel Mode:</strong> When traveling, you can temporarily update your location to meet people in new cities.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <Camera className="w-5 h-5 text-neon-red" />
                  3. Photo Storage and Moderation
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Photo Storage:</strong> Profile photos are stored securely on our servers with encryption at rest and in transit.</p>
                <p><strong>Content Moderation:</strong> All photos undergo automated and manual review for safety and community guidelines compliance.</p>
                <p><strong>Photo Deletion:</strong> When you delete photos or close your account, images are permanently removed from our systems within 30 days.</p>
                <p><strong>Sharing Prevention:</strong> We implement measures to prevent screenshots and unauthorized sharing of photos.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <MessageCircle className="w-5 h-5 text-neon-red" />
                  4. How We Use Your Information
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Matching Algorithm:</strong> We use your preferences, interests, and location to suggest compatible matches.</p>
                <p><strong>Safety and Security:</strong> Information is used to prevent fraud, abuse, and to maintain a safe community environment.</p>
                <p><strong>Service Improvement:</strong> Anonymous usage data helps us improve app features and user experience.</p>
                <p><strong>Communication:</strong> We may send important updates about your account, safety alerts, and service notifications.</p>
                <p><strong>Legal Compliance:</strong> Information may be used to comply with legal obligations and protect user rights.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <Users className="w-5 h-5 text-neon-red" />
                  5. Information Sharing and Disclosure
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>With Other Users:</strong> Only information you choose to include in your profile is visible to other users.</p>
                <p><strong>Service Providers:</strong> We may share data with trusted service providers who help operate our platform (hosting, analytics, moderation).</p>
                <p><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect user safety.</p>
                <p><strong>Business Transfers:</strong> In case of merger or acquisition, user data may be transferred to new ownership with equivalent privacy protections.</p>
                <p><strong>What We Don't Share:</strong> We never sell your personal information to third parties or use it for advertising outside our platform.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">6. Data Security</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Encryption:</strong> All data is encrypted both in transit and at rest using industry-standard protocols.</p>
                <p><strong>Access Controls:</strong> Strict access controls ensure only authorized personnel can access user data, and only when necessary for service operations.</p>
                <p><strong>Security Audits:</strong> Regular security audits and monitoring help us identify and address potential vulnerabilities.</p>
                <p><strong>Incident Response:</strong> We have procedures in place to quickly respond to and notify users of any security incidents.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">7. Your Privacy Rights</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Access:</strong> You can view and download all personal information we have about you.</p>
                <p><strong>Correction:</strong> You can update or correct your profile information at any time.</p>
                <p><strong>Deletion:</strong> You can delete your account and all associated data permanently.</p>
                <p><strong>Portability:</strong> You can export your data in a machine-readable format.</p>
                <p><strong>Restriction:</strong> You can limit how we process certain types of your information.</p>
                <p><strong>Objection:</strong> You can object to certain processing activities, such as marketing communications.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">8. Cookies and Tracking</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Essential Cookies:</strong> We use necessary cookies to maintain your login session and app functionality.</p>
                <p><strong>Analytics:</strong> Anonymous usage analytics help us understand how users interact with our app to improve the experience.</p>
                <p><strong>No Third-Party Tracking:</strong> We do not use third-party advertising cookies or tracking pixels.</p>
                <p><strong>Cookie Control:</strong> You can manage cookie preferences through your device settings.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">9. Data Retention</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Active Accounts:</strong> We retain your information while your account is active and as needed to provide services.</p>
                <p><strong>Inactive Accounts:</strong> Accounts inactive for over 2 years may be automatically deleted after notification.</p>
                <p><strong>Deleted Accounts:</strong> When you delete your account, most data is removed immediately, with complete deletion within 30 days.</p>
                <p><strong>Legal Obligations:</strong> Some information may be retained longer when required by law or for safety purposes.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">10. International Data Transfers</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Data Processing:</strong> Your data is processed in the European Union with GDPR protections.</p>
                <p><strong>Cross-Border Transfers:</strong> If data needs to be transferred outside the EU, we ensure adequate protection through standard contractual clauses.</p>
                <p><strong>Global Service:</strong> Our service may be accessed globally, but data protection standards remain consistent.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">11. Children's Privacy</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>Our service is not intended for users under 18 years old. We do not knowingly collect personal information from minors. If we discover that a minor has created an account, we will delete it immediately.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">12. Changes to Privacy Policy</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>We may update this Privacy Policy periodically. Significant changes will be communicated through the app or email. Your continued use of our service constitutes acceptance of any updates.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">13. Contact Us</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>For questions about this Privacy Policy or to exercise your privacy rights:</p>
                <p><strong>Email:</strong> support@alphamanonly.com</p>
                <p><strong>Data Protection Officer:</strong> support@alphamanonly.com</p>
                <p><strong>Address:</strong> Kfir Yehuda, Fuggerstraße 9, 10777 Berlin</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center py-8">
            <p className="text-ice-white/60 text-sm">
              Your privacy and safety are our top priorities. We are committed to maintaining the trust you place in us 
              by handling your information with the utmost care and respect.
            </p>
          </div>

          <LegalFooter />
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
